/*
 * Email-signup offer widget — three explicit states (modal | tab | dismissed)
 * with localStorage persistence + expiry so it doesn't nag returning visitors.
 *
 * Progressive enhancement: the popup is a JS-only surface (the homepage's own
 * newsletter section covers no-JS visitors). Native <dialog scroll-lock> gives
 * the backdrop overlay, Escape, and scroll-lock (see critical.css) for free.
 *
 * The form itself posts NATIVELY -- there is no AJAX layer and no onSubscribe
 * hook any more. See the note above the class for why.
 *
 * Config precedence: window.PFEmailOffer overrides > data-* attributes > defaults.
 * window.PFEmailOffer is the pluggable interface layer:
 *   - initialState: 'modal'|'tab'|'dismissed'  testing override; bypasses persistence
 *   - autoOpenDelay: <milliseconds>            overrides data-auto-open-delay (seconds)
 *   - expiryDays: <days>                        overrides data-expiry-days
 * Testing override is also available via ?email_offer=modal|tab|dismissed.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'pf:email-offer';
  const DAY_MS = 864e5;
  const STATES = ['modal', 'tab', 'dismissed'];
  const SUCCESS_MS = 2600; // how long the success line shows before the modal dismisses

  const cfg = (window.PFEmailOffer = window.PFEmailOffer || {});

  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  /*
   * NO AJAX HERE, DELIBERATELY. The store has Shopify's captcha switched on.
   *
   * `window.Shopify.captcha === true` on this storefront. That makes Shopify
   * inject a document-level `submit` listener which calls preventDefault(),
   * runs its invisible captcha, writes a token into the form, and submits the
   * form itself.
   *
   * A fetch() to /contact skips every step of that, so the POST arrives with
   * no captcha token and Shopify answers **HTTP 400**. That is the whole bug.
   * Not the encoding, not rate limiting, not Cloudflare's bot challenge, not
   * contact[tags] -- all four were investigated and none of them were it.
   *
   * The tell was there the whole time: NATIVE submits worked and AJAX submits
   * did not. Every customer record on the store was created by a native post,
   * because only a native post carries the token.
   *
   * Dawn does not AJAX the newsletter form either -- it is a plain
   * {% form 'customer' %} that posts natively, with the confirmation rendered
   * from form.posted_successfully?. This theme already did that in the footer
   * and home-newsletter sections, which no JS touches and which always worked.
   *
   * Verified by walking the submit event up the ancestor chain: defaultPrevented
   * is false at every node from the form to <html>, and true at #document.
   *
   * Do not reintroduce fetch() here unless the captcha is off AND you have
   * checked window.Shopify.captcha on the live storefront.
   */

  // Only one widget engages per page; a duplicate section instance no-ops.
  let engagedInstance = null;

  class EmailOffer extends HTMLElement {
    connectedCallback() {
      if (engagedInstance && engagedInstance !== this) return;
      if (this.dataset.enabled === 'false') return;

      this.dialog = this.querySelector('.email-offer__modal');
      this.tab = this.querySelector('.email-offer__tab');
      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-eo-status]');
      this.tabOpenBtn = this.tab && this.tab.querySelector('[data-eo-open]');
      if (!this.dialog || !this.tab || typeof this.dialog.showModal !== 'function') return;

      engagedInstance = this;
      this.state = 'dismissed'; // initial DOM (tab hidden, dialog closed) matches this
      this.openTimer = null;
      this.doneTimer = null;
      this.submitting = false; // guards a double post while the page navigates
      this.done = false; // set once subscribed; freezes the close paths (see minimize)

      this.autoOpenDelay =
        cfg.autoOpenDelay != null
          ? cfg.autoOpenDelay
          : (parseFloat(this.dataset.autoOpenDelay) || 1.5) * 1000;
      this.expiryDays =
        cfg.expiryDays != null ? cfg.expiryDays : parseInt(this.dataset.expiryDays, 10) || 30;

      this.bindModal();
      this.bindTab();
      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.doneTimer);
      if (engagedInstance === this) engagedInstance = null;
    }

    /* ---- load logic ---- */
    init() {
      // Theme editor: no persistence, no auto-open. Modal wiring stays live
      // (bindModal already ran) so the merchant can open/close/minimize it,
      // and section select/deselect drives it. writeState() no-ops in design mode.
      if (isDesignMode()) {
        this.bindDesignMode();
        return;
      }

      // Landing on ?customer_posted=true means Shopify just processed the
      // native submit. This is now the ONLY success path, so this branch is
      // what confirms the signup -- not a nicety after a hand-off.
      if (this.querySelector('.email-offer__success')) {
        const alreadyCounted = this.readState() === 'subscribed';
        this.writeState('subscribed');
        this.clearOutcome(); // drop the Liquid line; the modal owns the message now
        if (alreadyCounted) {
          // Re-load of the same ?customer_posted=true URL. Already confirmed
          // once; don't re-open the modal every refresh.
          this.setState('dismissed');
          return;
        }
        this.setState('modal');
        this.showSubscribed(); // hides the form, shows the confirmation, auto-dismisses
        return;
      }

      const override = cfg.initialState || this.urlOverride();
      if (override && STATES.indexOf(override) !== -1) {
        this.setState(override); // testing: render directly, don't persist
        return;
      }

      const persisted = this.readState();
      if (persisted === 'dismissed' || persisted === 'subscribed') {
        this.setState('dismissed');
      } else if (persisted === 'minimized') {
        this.setState('tab');
      } else if (window.matchMedia('(max-width: 750px)').matches) {
        // Mobile first-time: start in the tab, never auto-interstitial (SEO).
        this.setState('tab');
      } else {
        // We know this one is going to open — start the image now so it has the
        // auto-open delay as a head start and the pane is never briefly empty.
        this.warmMedia();
        this.openTimer = window.setTimeout(() => this.openModal(), this.autoOpenDelay);
      }
    }

    /*
     * The media image lives inside a closed <dialog>, i.e. a display:none
     * subtree. A loading="lazy" image there never intersects the viewport, so
     * the browser never queues it — and showModal() does not retroactively
     * trigger it. The pane stays empty for the entire life of the page.
     * Verified on live 2026-08-24: complete === false, currentSrc === '' with
     * the dialog open.
     *
     * Fixing it in the markup would mean eager-loading the image for every
     * homepage visitor, including the ones who dismiss the widget and the
     * mobile ones who never open it (init() starts them in the tab). So the
     * fetch is started here instead, at the two points where we know the modal
     * is about to be on screen.
     */
    warmMedia() {
      const img = this.querySelector('.email-offer__img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    urlOverride() {
      try {
        return new URLSearchParams(window.location.search).get('email_offer');
      } catch (error) {
        return null;
      }
    }

    bindDesignMode() {
      const id = this.dataset.sectionId;
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail && event.detail.sectionId === id) this.openModal();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail && event.detail.sectionId === id) this.setState('dismissed');
      });
    }

    /* ---- wiring ---- */
    bindModal() {
      const closeBtn = this.querySelector('[data-eo-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.minimize());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault(); // Escape minimizes to the tab, not full close
        this.minimize();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.minimize(); // backdrop click
      });
      // Safety net: if the dialog ever closes while we still think we're modal
      // (e.g. Chrome force-closing on a rapid second Escape), reconcile to tab.
      this.dialog.addEventListener('close', () => {
        if (this.state === 'modal') this.minimize();
      });
      if (this.form) {
        // No preventDefault on the happy path. The form posts natively, exactly
        // like the footer newsletter and like Dawn -- see the note at the top of
        // this file. We only intercept to reject a malformed address before the
        // page navigates, and to stop a double submit.
        this.form.addEventListener('submit', (event) => {
          if (this.emailInput && !this.emailInput.checkValidity()) {
            event.preventDefault();
            this.emailInput.setAttribute('aria-invalid', 'true');
            this.emailInput.reportValidity();
            this.setStatus('Enter a valid email address.', 'error');
            return;
          }
          if (this.submitting) {
            event.preventDefault();
            return;
          }
          this.submitting = true;
          if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
          if (this.submitBtn) this.submitBtn.setAttribute('aria-busy', 'true');
          this.setStatus('Signing you up…', 'pending');
          // Let it go. Shopify redirects to ?customer_posted=true and init()
          // picks the confirmation up on the way back in.
        });
      }
    }

    bindTab() {
      if (this.tabOpenBtn) this.tabOpenBtn.addEventListener('click', () => this.restore());
      const dismissBtn = this.tab.querySelector('[data-eo-dismiss]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', (event) => {
          event.stopPropagation(); // don't also trigger the tab's open/restore
          this.dismiss();
        });
      }
    }

    /* ---- state machine ---- */
    setState(next) {
      this.state = next;
      this.render();
    }

    render() {
      if (this.state === 'modal') {
        this.warmMedia(); // covers the tab-tap and testing-override paths
        if (!this.dialog.open) this.dialog.showModal();
        this.tab.hidden = true;
      } else if (this.state === 'tab') {
        if (this.dialog.open) this.dialog.close();
        this.tab.hidden = false;
      } else {
        if (this.dialog.open) this.dialog.close();
        this.tab.hidden = true;
      }
    }

    /* ---- transitions ---- */
    openModal() {
      window.clearTimeout(this.openTimer);
      this.setState('modal'); // showModal() handles focus natively
    }

    minimize() {
      // Once subscribed we're mid-success-display; a backdrop/Escape/close here
      // must NOT persist 'minimized' over the already-saved 'subscribed'.
      if (this.done) {
        this.setState('dismissed');
        return;
      }
      this.setState('tab');
      this.writeState('minimized');
      if (this.tabOpenBtn) this.tabOpenBtn.focus();
    }

    restore() {
      this.setState('modal'); // leaves persisted 'minimized' so a reload still shows the tab
    }

    dismiss() {
      this.setState('dismissed');
      this.writeState('dismissed');
    }

    /*
     * The stale-confirmation guard.
     *
     * .email-offer__success is rendered by Liquid from
     * form.posted_successfully?, which is driven by ?customer_posted=true in
     * the URL. That param survives every reload, so the green "Check your
     * inbox" line renders above an EMPTY form, before the shopper has typed
     * anything. Measured in Matt's screen recording 2026-09-02: frame 1,
     * empty field, green line already on screen.
     *
     * init() counts that line as the confirmation, then this drops it from
     * the DOM so a reopened modal cannot show it again over a blank field.
     */
    clearOutcome() {
      const done = this.querySelector('[data-eo-done]');
      if (done) done.hidden = true;
      this.querySelectorAll('.email-offer__success, .email-offer__error').forEach((el) => el.remove());
    }

    // Reveal the success line in the open modal, hide the form, then dismiss.
    // 'subscribed' is already persisted, so leaving early still never re-shows.
    // No code is shown here on purpose — it is emailed, so it stays tied to the
    // address that earned it. See the note in sections/email-offer.liquid.
    showSubscribed() {
      this.done = true;
      this.setStatus('', '');
      if (this.dialog) this.dialog.classList.add('is-subscribed');
      const done = this.querySelector('[data-eo-done]');
      if (done) done.hidden = false;
      window.clearTimeout(this.doneTimer);
      this.doneTimer = window.setTimeout(() => this.setState('dismissed'), SUCCESS_MS);
    }

    setStatus(message, tone) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.dataset.tone = tone || '';
    }

    /* ---- persistence ---- */
    readState() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !data.status) return null;
        // Subscribed persists indefinitely — never re-engage a subscriber,
        // regardless of any stored expiresAt. Other states honor expiry.
        if (data.status === 'subscribed') return 'subscribed';
        if (data.expiresAt != null && Date.now() > data.expiresAt) {
          this.clearState();
          return null;
        }
        return data.status;
      } catch (error) {
        return null; // localStorage unavailable → in-memory behavior
      }
    }

    writeState(status) {
      if (isDesignMode()) return; // never persist in the theme editor
      try {
        const expiresAt = status === 'subscribed' ? null : Date.now() + this.expiryDays * DAY_MS;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, expiresAt }));
      } catch (error) {
        /* private mode / quota — degrade to in-memory (per-session) */
      }
    }

    clearState() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* no-op */
      }
    }
  }

  if (!window.customElements.get('email-offer')) {
    window.customElements.define('email-offer', EmailOffer);
  }
})();
